import { IPublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser'
import { config } from '../config'
import { apiTokenRequest, apiWriteTokenRequest, loginRequest } from '../auth/msalConfig'
import {
  ClaimRequest, ClaimResponse, DashboardResponse, MeResponse, ApiError,
  ScheduleResponse, OverrideCreateRequest, OverrideCreateResponse, OverrideCancelResponse,
} from './types'

async function getAccessToken(
  msalInstance: IPublicClientApplication,
  write = false,
): Promise<string> {
  const account = msalInstance.getAllAccounts()[0]
  if (!account) throw new Error('No signed-in account')
  const request = write ? apiWriteTokenRequest : apiTokenRequest

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...request,
      account,
    })
    return response.accessToken
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect(write ? { scopes: [...loginRequest.scopes, ...apiWriteTokenRequest.scopes] } : loginRequest)
      throw new Error('Interactive auth required — redirecting')
    }
    throw err
  }
}

/**
 * Perform an authenticated API call. Throws a structured ApiError on non-2xx.
 * Supports GET (default) and POST with JSON body.
 */
export async function apiFetch<T>(
  msalInstance: IPublicClientApplication,
  path: string,
  options: { method?: 'GET' | 'POST'; body?: unknown; write?: boolean } = {},
): Promise<T> {
  const token = await getAccessToken(msalInstance, options.write ?? false)
  const method = options.method ?? 'GET'

  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
    'Accept': 'application/json',
  }
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }

  const response = await fetch(`${config.apiBaseUrl}${path}`, {
    method,
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  })

  if (!response.ok) {
    let detail: unknown = null
    try {
      const body = await response.json()
      detail = body?.detail
    } catch {
      // Body wasn't JSON
    }

    const isObj = typeof detail === 'object' && detail !== null
    const apiError: ApiError = {
      status: response.status,
      code: isObj ? (detail as Record<string, unknown>).error as string | undefined : undefined,
      message: isObj
        ? ((detail as Record<string, unknown>).message as string | undefined) ?? response.statusText
        : (typeof detail === 'string' ? detail : response.statusText),
    }
    throw apiError
  }

  return response.json() as Promise<T>
}

export function getMe(msalInstance: IPublicClientApplication): Promise<MeResponse> {
  return apiFetch<MeResponse>(msalInstance, '/api/v1/me')
}

export function getDashboard(msalInstance: IPublicClientApplication): Promise<DashboardResponse> {
  return apiFetch<DashboardResponse>(msalInstance, '/api/v1/me/dashboard')
}

export function claimInvitation(
  msalInstance: IPublicClientApplication,
  invitation_token: string,
): Promise<ClaimResponse> {
  const body: ClaimRequest = { invitation_token }
  return apiFetch<ClaimResponse>(msalInstance, '/api/v1/me/claim', {
    method: 'POST',
    body,
  })
}

// ---------------------------------------------------------------------------
// v1.2.0 — customer slot overrides
// ---------------------------------------------------------------------------

export function getSchedule(
  msalInstance: IPublicClientApplication,
  systemId?: number,
): Promise<ScheduleResponse> {
  const q = systemId ? `?system_id=${encodeURIComponent(systemId)}` : ''
  return apiFetch<ScheduleResponse>(msalInstance, `/api/v1/me/schedule${q}`)
}

export function createOverride(
  msalInstance: IPublicClientApplication,
  body: OverrideCreateRequest,
): Promise<OverrideCreateResponse> {
  return apiFetch<OverrideCreateResponse>(msalInstance, '/api/v1/me/overrides', {
    method: 'POST',
    body,
    write: true,
  })
}

export function cancelOverride(
  msalInstance: IPublicClientApplication,
  requestId: string,
): Promise<OverrideCancelResponse> {
  return apiFetch<OverrideCancelResponse>(
    msalInstance,
    `/api/v1/me/overrides/${encodeURIComponent(requestId)}/cancel`,
    { method: 'POST', write: true },
  )
}
